import { useEffect, useRef, useState } from "react";

import type {
  TourCloseReason,
  UseTourOptions,
  UseTourResult,
} from "./Tour.types";
import { TOUR_STORAGE_PREFIX } from "./constants";

function wasSeen(key: string): boolean {
  try {
    return localStorage.getItem(`${TOUR_STORAGE_PREFIX}${key}`) === "1";
  } catch {
    return false;
  }
}

function markSeen(key: string): void {
  try {
    localStorage.setItem(`${TOUR_STORAGE_PREFIX}${key}`, "1");
  } catch {
    // A tour that cannot be remembered is still a tour worth showing
  }
}

export function useTour({
  steps,
  storageKey,
  autoStart = false,
  onStart,
  onStepChange,
  onComplete,
  onSkip,
}: UseTourOptions): UseTourResult {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  function handleStepChange(next: number) {
    setStep(next);

    onStepChange?.(next);
  }

  function handleClose(reason: TourCloseReason) {
    setOpen(false);

    if (storageKey) {
      markSeen(storageKey);
    }

    if (reason === "complete") {
      onComplete?.();
    } else {
      onSkip?.(step);
    }
  }

  function start(options?: { force?: boolean; from?: number }) {
    if (!options?.force && storageKey && wasSeen(storageKey)) {
      return;
    }

    setStep(options?.from ?? 0);
    setOpen(true);

    onStart?.();
  }

  // Once per mount, and a frame later: what the same commit mounts through a
  // portal, a drawer for instance, is not there for the first step to find.
  const started = useRef(false);

  useEffect(() => {
    if (!autoStart || started.current) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      started.current = true;
      start();
    });

    return () => cancelAnimationFrame(frame);
    // `start` is rebuilt on every render; the trigger is the flag alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  return {
    open,
    step,
    start,
    next: () => {
      if (step < steps.length - 1) {
        handleStepChange(step + 1);
      } else {
        handleClose("complete");
      }
    },
    previous: () => {
      if (step > 0) {
        handleStepChange(step - 1);
      }
    },
    close: () => handleClose("skip"),
    props: {
      steps,
      open,
      step,
      onStepChange: handleStepChange,
      onClose: handleClose,
    },
  };
}
